import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, ClipboardList, Users, Menu, Settings, LogOut, ChevronDown, ChevronRight, Shield, X, Sliders, UserCircle, Building2, FolderKanban, FileSearch, House, BarChart3, Handshake } from 'lucide-react';
import { menusAPI } from '../utils/api';
import api from '../utils/api';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [allowedMenuIds, setAllowedMenuIds] = useState(null);
  const [config, setConfig] = useState({
    company_name: 'IMAGINA',
    platform_name: 'Sistema de Postventa',
    logo_url: null
  });

  const defaultMenuItems = [
    { 
      id: 'menu-dashboard',
      path: '/dashboard', 
      icon: 'LayoutDashboard', 
      label: 'Dashboard', 
      roles: ['admin', 'jefe_postventa', 'supervisor'],
      nivel: 1,
      children: []
    },
    { 
      id: 'menu-postventa',
      path: null,
      icon: 'ClipboardList', 
      label: 'Postventa', 
      roles: ['admin', 'jefe_postventa', 'supervisor'],
      nivel: 1,
      children: [
        { id: 'menu-solicitudes', path: '/solicitudes', icon: 'ClipboardList', label: 'Requerimientos', roles: ['admin', 'jefe_postventa', 'supervisor'] },
        { id: 'menu-configuracion', path: '/configuracion', icon: 'Settings', label: 'Configuración', roles: ['admin', 'jefe_postventa'] }
      ]
    },
    { 
      id: 'menu-gestion-proyectos',
      path: null,
      icon: 'FolderKanban', 
      label: 'G. de Proyectos', 
      roles: ['admin', 'jefe_postventa'],
      nivel: 1,
      children: [
        { id: 'menu-proyectos', path: '/proyectos', icon: 'Building2', label: 'Proyectos', roles: ['admin'] },
        { id: 'menu-proveedores', path: '/proveedores', icon: 'Users', label: 'Proveedores', roles: ['admin', 'jefe_postventa'] }
      ]
    },
    {
      id: 'menu-entregas',
      path: null,
      icon: 'House',
      label: 'Entregas',
      roles: ['admin', 'jefe_postventa', 'supervisor'],
      nivel: 1,
      children: [
        { id: 'menu-entregas-dashboard', path: '/entregas/dashboard', icon: 'BarChart3', label: 'Dashboard', roles: ['admin', 'jefe_postventa'] },
        { id: 'menu-entregas-lista', path: '/entregas', icon: 'Handshake', label: 'Entregas UI', roles: ['admin', 'jefe_postventa', 'supervisor'] },
        { id: 'menu-entregas-config', path: '/entregas/configuracion', icon: 'Settings', label: 'Configuración', roles: ['admin', 'jefe_postventa'] }
      ]
    },
    { 
      id: 'menu-admin',
      path: null,
      icon: 'Settings', 
      label: 'Administración', 
      roles: ['admin'],
      nivel: 1,
      children: [
        { id: 'menu-general', path: '/configuracion-general', icon: 'Sliders', label: 'General', roles: ['admin'] },
        { id: 'menu-usuarios', path: '/usuarios', icon: 'Users', label: 'Usuarios', roles: ['admin'] },
        { id: 'menu-roles', path: '/roles', icon: 'Shield', label: 'Roles', roles: ['admin'] },
        { id: 'menu-menus', path: '/menus', icon: 'Menu', label: 'Menús', roles: ['admin'] },
        { id: 'menu-auditoria', path: '/auditoria', icon: 'FileSearch', label: 'Auditoría', roles: ['admin'] }
      ]
    }
  ];

  useEffect(() => {
    loadDynamicMenus();
    loadConfig();
    
    // Listen for config updates
    const handleConfigUpdate = (e) => {
      if (e.detail) {
        setConfig(prev => ({ ...prev, ...e.detail }));
      }
    };
    window.addEventListener('configUpdated', handleConfigUpdate);
    return () => window.removeEventListener('configUpdated', handleConfigUpdate);
  }, [user]);

  const loadConfig = async () => {
    try {
      const response = await api.get('/configuracion/general');
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadDynamicMenus = async () => {
    try {
      // Load role permissions in parallel with menus
      const [menuResponse, rolePermisos] = await Promise.all([
        menusAPI.getAll(),
        (async () => {
          if (user?.rol_id) {
            try {
              const r = await api.get(`/roles/${user.rol_id}`);
              const permisos = r.data?.permisos || [];
              const ids = new Set(permisos.filter(p => p.ver).map(p => p.menu_id));
              return ids;
            } catch { return null; }
          }
          return null;
        })(),
      ]);

      if (rolePermisos) setAllowedMenuIds(rolePermisos);

      const dynamicMenus = menuResponse.data;
      
      if (dynamicMenus && dynamicMenus.length > 0) {
        const parentMenus = dynamicMenus.filter(m => !m.padre_id && m.activo);
        const childMenus = dynamicMenus.filter(m => m.padre_id && m.activo);
        
        const structuredMenus = parentMenus.map(parent => ({
          id: parent.id,
          path: childMenus.some(c => c.padre_id === parent.id) ? null : (parent.ruta === '#' ? null : parent.ruta),
          icon: parent.icono || 'LayoutDashboard',
          label: parent.nombre,
          roles: parent.roles_permitidos || ['admin'],
          orden: parent.orden,
          children: childMenus
            .filter(child => child.padre_id === parent.id)
            .sort((a, b) => a.orden - b.orden)
            .map(child => ({
              id: child.id,
              path: child.ruta === '#' ? null : child.ruta,
              icon: child.icono || 'ClipboardList',
              label: child.nombre,
              roles: child.roles_permitidos || ['admin']
            }))
        })).sort((a, b) => a.orden - b.orden);

        if (structuredMenus.length > 0) {
          setMenuItems(structuredMenus);
        } else {
          setMenuItems(defaultMenuItems);
        }
      } else {
        setMenuItems(defaultMenuItems);
      }
    } catch (error) {
      console.error('Error loading menus:', error);
      setMenuItems(defaultMenuItems);
    } finally {
      setLoadingMenus(false);
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      'LayoutDashboard': LayoutDashboard,
      'ClipboardList': ClipboardList,
      'Users': Users,
      'Menu': Menu,
      'Settings': Settings,
      'Shield': Shield,
      'Sliders': Sliders,
      'Building2': Building2,
      'FolderKanban': FolderKanban,
      'FileSearch': FileSearch,
      'Truck': House,
      'BarChart3': BarChart3,
      'Gift': Handshake,
      'House': House,
      'Handshake': Handshake,
    };
    return iconMap[iconName] || LayoutDashboard;
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const normalizeRole = (role) => {
    if (!role) return '';
    return role.toLowerCase().replace(/[\s_-]/g, '');
  };

  const filteredMenu = menuItems.filter(item => {
    if (user?.rol_nombre === 'Administrador') return true;

    if (allowedMenuIds !== null) {
      // Permisos de rol cargados: usar SOLO allowedMenuIds
      if (allowedMenuIds.has(item.id)) return true;
      if (item.children?.length && item.children.some(child => allowedMenuIds.has(child.id))) return true;
      return false;
    }

    // Fallback solo si allowedMenuIds no se cargó
    const userRoleNormalized = normalizeRole(user?.rol_nombre);
    return item.roles?.some(role => normalizeRole(role) === userRoleNormalized);
  }).map(item => {
    if (!item.children?.length || user?.rol_nombre === 'Administrador') return item;
    const filteredChildren = item.children.filter(child => {
      if (allowedMenuIds !== null) return allowedMenuIds.has(child.id);
      const userRoleNormalized = normalizeRole(user?.rol_nombre);
      return child.roles?.some(role => normalizeRole(role) === userRoleNormalized);
    });
    return { ...item, children: filteredChildren };
  });

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-[#E8E8E7] flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Header with Logo and Platform Name */}
        <div className="p-4 lg:p-6 border-b border-[#E8E8E7]">
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden absolute top-3 right-3 p-2 hover:bg-gray-100 rounded-md"
          >
            <X size={20} className="text-[#5A5A5A]" />
          </button>
          
          {/* Logo and Platform Name - Centered */}
          <div className="flex flex-col items-center text-center">
            {config.logo_url ? (
              <img 
                src={config.logo_url} 
                alt={config.company_name}
                className="h-16 w-auto max-w-[180px] object-contain mb-2"
              />
            ) : (
              <h1 
                className="text-xl lg:text-2xl font-bold text-[#9d8272] mb-2" 
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {config.company_name}
              </h1>
            )}
            <p className="text-xs lg:text-sm text-[#888888]">
              {config.platform_name}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
          {filteredMenu.map((item) => {
            const Icon = getIconComponent(item.icon);
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedMenus[item.id];
            const isActive = location.pathname === item.path;
            const hasActiveChild = hasChildren && item.children.some(
              child => location.pathname === child.path
            );

            return (
              <div key={item.id}>
                {item.path ? (
                  <Link
                    to={item.path}
                    onClick={handleNavClick}
                    data-testid={`nav-${item.path}`}
                    className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-md transition-all text-sm lg:text-base ${
                      isActive
                        ? 'bg-[#9d8272] text-white shadow-sm'
                        : 'text-[#5A5A5A] hover:bg-[#E8E8E7] hover:text-[#2D2D2D]'
                    }`}
                  >
                    <Icon size={18} className="lg:w-5 lg:h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => toggleMenu(item.id)}
                    data-testid={`nav-toggle-${item.id}`}
                    className={`w-full flex items-center justify-between px-3 lg:px-4 py-2.5 lg:py-3 rounded-md transition-all text-sm lg:text-base ${
                      hasActiveChild
                        ? 'bg-[#E8E8E7] text-[#2D2D2D]'
                        : 'text-[#5A5A5A] hover:bg-[#E8E8E7] hover:text-[#2D2D2D]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={18} className="lg:w-5 lg:h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {hasChildren && (
                      isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                    )}
                  </button>
                )}

                {hasChildren && isExpanded && (
                  <div className="ml-3 lg:ml-4 mt-1 space-y-1">
                    {item.children.map((child) => {
                        const ChildIcon = getIconComponent(child.icon);
                        const childIsActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.id}
                            to={child.path}
                            onClick={handleNavClick}
                            data-testid={`nav-${child.path}`}
                            className={`flex items-center gap-3 px-3 lg:px-4 py-2 rounded-md transition-all text-xs lg:text-sm ${
                              childIsActive
                                ? 'bg-[#9d8272] text-white shadow-sm'
                                : 'text-[#5A5A5A] hover:bg-[#E8E8E7] hover:text-[#2D2D2D]'
                            }`}
                          >
                            <ChildIcon size={16} className="lg:w-[18px] lg:h-[18px]" />
                            <span className="font-medium">{child.label}</span>
                          </Link>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-3 lg:p-4 border-t border-[#E8E8E7]">
          <Link
            to="/perfil"
            onClick={handleNavClick}
            className="block mb-2 lg:mb-3 p-2 lg:p-3 bg-[#F5F5F5] rounded-md hover:bg-[#E8E8E7] transition-colors group"
          >
            <div className="flex items-center gap-3">
              {user?.foto_url ? (
                <img 
                  src={user.foto_url} 
                  alt={user?.nombre}
                  className="w-10 h-10 rounded-full object-cover border-2 border-[#c5b8a2]"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#c5b8a2] flex items-center justify-center">
                  <UserCircle size={24} className="text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-[#2D2D2D] truncate group-hover:text-[#9d8272]">
                  {user?.nombre}
                </p>
                <p className="text-[10px] lg:text-xs text-[#888888] truncate">{user?.email}</p>
                <p className="text-[10px] lg:text-xs text-[#9d8272] capitalize">{user?.rol_nombre}</p>
              </div>
            </div>
          </Link>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 lg:px-4 py-2 text-sm text-[#d32f2f] hover:bg-red-50 rounded-md transition-all"
          >
            <LogOut size={16} className="lg:w-[18px] lg:h-[18px]" />
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
